import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { FleetTrailer, FleetTrailerType } from '../models/trailer.model';
import { FleetTrailerService } from '../trailer.service';

@Component({
    selector: 'app-trailer-type-view',
    standalone: true,
    templateUrl: './trailer-type-view.component.html',
    styleUrls: ['../trailer-page.css'],
    imports: [
        CommonModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class TrailerTypeViewComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    trailerTypeId = 0;
    trailerType?: FleetTrailerType;
    trailers: FleetTrailer[] = [];
    isLoading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.trailerTypeId = Number(this.route.snapshot.paramMap.get('id'));
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Trailer Types', routerLink: '/fleet/trailer-types' },
            { label: 'View' }
        ];
        if (this.trailerTypeId) {
            this.load();
        }
    }

    load(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.isLoading = true;
        this.trailerService.getTrailerType(this.trailerTypeId).subscribe({
            next: response => {
                this.trailerType = response.data;
                if (companyId) {
                    this.loadRelatedTrailers(companyId);
                } else {
                    this.isLoading = false;
                }
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load trailer type.' });
            }
        });
    }

    edit(): void {
        this.router.navigate(['/fleet/trailer-type/edit', this.trailerTypeId]);
    }

    back(): void {
        this.router.navigate(['/fleet/trailer-types']);
    }

    openTrailer(row: FleetTrailer): void {
        if (row.trailerId) {
            this.router.navigate(['/fleet/trailer/view', row.trailerId]);
        }
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Status', value: this.trailerType?.active === false ? 'Inactive' : 'Active', hint: 'Master-data availability', tone: this.trailerType?.active === false ? 'danger' : 'success' },
            { label: 'Active Trailers', value: this.trailerType?.activeTrailerCount ?? this.trailers.length, hint: 'Backend count for active assets', tone: 'info' },
            { label: 'Sort Order', value: this.trailerType?.sortOrder ?? 0, hint: 'List ordering hint', tone: 'default' }
        ];
    }

    statusSeverity(active?: boolean): 'success' | 'danger' {
        return active === false ? 'danger' : 'success';
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    private loadRelatedTrailers(companyId: number): void {
        this.trailerService.searchTrailers({
            owningCompanyId: companyId,
            page: 0,
            pageSize: 20,
            trailerTypeId: this.trailerTypeId
        }).subscribe({
            next: response => {
                this.trailers = response.items ?? [];
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'warn', summary: 'Related trailers', detail: 'Trailer type loaded but related trailers could not be loaded.' });
            }
        });
    }
}
